/**
 * PAYSTUB TAX CALCULATIONS
 * 
 * Accurate federal and state tax calculations for all 50 US states
 * Optimized for MINIMUM realistic tax withholdings
 * 
 * 2024-2026 Tax Brackets
 */

// Federal Tax Brackets 2024 (Single)
const FEDERAL_TAX_BRACKETS_SINGLE = [
  { min: 0, max: 11600, rate: 0.10 },
  { min: 11600, max: 47150, rate: 0.12 },
  { min: 47150, max: 100525, rate: 0.22 },
  { min: 100525, max: 191950, rate: 0.24 },
  { min: 191950, max: 243725, rate: 0.32 },
  { min: 243725, max: 609350, rate: 0.35 },
  { min: 609350, max: Infinity, rate: 0.37 }
];

// Federal Tax Brackets 2024 (Married Filing Jointly)
const FEDERAL_TAX_BRACKETS_MARRIED = [
  { min: 0, max: 23200, rate: 0.10 },
  { min: 23200, max: 94300, rate: 0.12 },
  { min: 94300, max: 201050, rate: 0.22 },
  { min: 201050, max: 383900, rate: 0.24 },
  { min: 383900, max: 487450, rate: 0.32 },
  { min: 487450, max: 731200, rate: 0.35 },
  { min: 731200, max: Infinity, rate: 0.37 }
];

// FICA Rates (2024)
export const FICA_RATES = {
  socialSecurity: 0.062, // 6.2%
  socialSecurityWageBase: 168600, // 2024 wage base
  medicare: 0.0145, // 1.45%
  additionalMedicare: 0.009, // 0.9% on wages over $200k
  additionalMedicareThreshold: 200000
};

// State Tax Data - All 50 States + DC
// Format: { rate: flat rate or brackets, sdi: state disability insurance rate, hasStateTax: boolean }
export const STATE_TAX_DATA = {
  'AL': { name: 'Alabama', hasStateTax: true, flatRate: 0.05, sdi: 0 },
  'AK': { name: 'Alaska', hasStateTax: false, flatRate: 0, sdi: 0 },
  'AZ': { name: 'Arizona', hasStateTax: true, flatRate: 0.025, sdi: 0 },
  'AR': { name: 'Arkansas', hasStateTax: true, flatRate: 0.044, sdi: 0 },
  'CA': { 
    name: 'California', 
    hasStateTax: true, 
    brackets: [
      { min: 0, max: 10412, rate: 0.01 },
      { min: 10412, max: 24684, rate: 0.02 },
      { min: 24684, max: 38959, rate: 0.04 },
      { min: 38959, max: 54081, rate: 0.06 },
      { min: 54081, max: 68350, rate: 0.08 },
      { min: 68350, max: 349137, rate: 0.093 },
      { min: 349137, max: 418961, rate: 0.103 },
      { min: 418961, max: 698271, rate: 0.113 },
      { min: 698271, max: Infinity, rate: 0.123 }
    ],
    sdi: 0.011, // 1.1% SDI rate
    sdiWageBase: 153164
  },
  'CO': { name: 'Colorado', hasStateTax: true, flatRate: 0.044, sdi: 0 },
  'CT': { name: 'Connecticut', hasStateTax: true, flatRate: 0.05, sdi: 0 },
  'DE': { name: 'Delaware', hasStateTax: true, flatRate: 0.066, sdi: 0 },
  'DC': { name: 'District of Columbia', hasStateTax: true, flatRate: 0.085, sdi: 0 },
  'FL': { name: 'Florida', hasStateTax: false, flatRate: 0, sdi: 0 },
  'GA': { name: 'Georgia', hasStateTax: true, flatRate: 0.055, sdi: 0 },
  'HI': { name: 'Hawaii', hasStateTax: true, flatRate: 0.0825, sdi: 0.005 },
  'ID': { name: 'Idaho', hasStateTax: true, flatRate: 0.058, sdi: 0 },
  'IL': { name: 'Illinois', hasStateTax: true, flatRate: 0.0495, sdi: 0 },
  'IN': { name: 'Indiana', hasStateTax: true, flatRate: 0.0315, sdi: 0 },
  'IA': { name: 'Iowa', hasStateTax: true, flatRate: 0.057, sdi: 0 },
  'KS': { name: 'Kansas', hasStateTax: true, flatRate: 0.057, sdi: 0 },
  'KY': { name: 'Kentucky', hasStateTax: true, flatRate: 0.045, sdi: 0 },
  'LA': { name: 'Louisiana', hasStateTax: true, flatRate: 0.0425, sdi: 0 },
  'ME': { name: 'Maine', hasStateTax: true, flatRate: 0.0715, sdi: 0 },
  'MD': { name: 'Maryland', hasStateTax: true, flatRate: 0.0575, sdi: 0 },
  'MA': { name: 'Massachusetts', hasStateTax: true, flatRate: 0.05, sdi: 0 },
  'MI': { name: 'Michigan', hasStateTax: true, flatRate: 0.0425, sdi: 0 },
  'MN': { name: 'Minnesota', hasStateTax: true, flatRate: 0.0785, sdi: 0 },
  'MS': { name: 'Mississippi', hasStateTax: true, flatRate: 0.05, sdi: 0 },
  'MO': { name: 'Missouri', hasStateTax: true, flatRate: 0.0495, sdi: 0 },
  'MT': { name: 'Montana', hasStateTax: true, flatRate: 0.0675, sdi: 0 },
  'NE': { name: 'Nebraska', hasStateTax: true, flatRate: 0.0584, sdi: 0 },
  'NV': { name: 'Nevada', hasStateTax: false, flatRate: 0, sdi: 0 },
  'NH': { name: 'New Hampshire', hasStateTax: false, flatRate: 0, sdi: 0 },
  'NJ': { name: 'New Jersey', hasStateTax: true, flatRate: 0.0637, sdi: 0.006 },
  'NM': { name: 'New Mexico', hasStateTax: true, flatRate: 0.049, sdi: 0 },
  'NY': { name: 'New York', hasStateTax: true, flatRate: 0.0685, sdi: 0.005 },
  'NC': { name: 'North Carolina', hasStateTax: true, flatRate: 0.0525, sdi: 0 },
  'ND': { name: 'North Dakota', hasStateTax: true, flatRate: 0.029, sdi: 0 },
  'OH': { name: 'Ohio', hasStateTax: true, flatRate: 0.04, sdi: 0 },
  'OK': { name: 'Oklahoma', hasStateTax: true, flatRate: 0.0475, sdi: 0 },
  'OR': { name: 'Oregon', hasStateTax: true, flatRate: 0.099, sdi: 0 },
  'PA': { name: 'Pennsylvania', hasStateTax: true, flatRate: 0.0307, sdi: 0 },
  'RI': { name: 'Rhode Island', hasStateTax: true, flatRate: 0.0599, sdi: 0.012 },
  'SC': { name: 'South Carolina', hasStateTax: true, flatRate: 0.065, sdi: 0 },
  'SD': { name: 'South Dakota', hasStateTax: false, flatRate: 0, sdi: 0 },
  'TN': { name: 'Tennessee', hasStateTax: false, flatRate: 0, sdi: 0 },
  'TX': { name: 'Texas', hasStateTax: false, flatRate: 0, sdi: 0 },
  'UT': { name: 'Utah', hasStateTax: true, flatRate: 0.0485, sdi: 0 },
  'VT': { name: 'Vermont', hasStateTax: true, flatRate: 0.0875, sdi: 0 },
  'VA': { name: 'Virginia', hasStateTax: true, flatRate: 0.0575, sdi: 0 },
  'WA': { name: 'Washington', hasStateTax: false, flatRate: 0, sdi: 0 },
  'WV': { name: 'West Virginia', hasStateTax: true, flatRate: 0.065, sdi: 0 },
  'WI': { name: 'Wisconsin', hasStateTax: true, flatRate: 0.0765, sdi: 0 },
  'WY': { name: 'Wyoming', hasStateTax: false, flatRate: 0, sdi: 0 }
};

/**
 * Calculate Federal Income Tax using progressive brackets
 * Applies MINIMUM withholding strategy
 */
export function calculateFederalTax(annualIncome, maritalStatus = 'single', exemptions = 1) {
  const brackets = maritalStatus === 'married' 
    ? FEDERAL_TAX_BRACKETS_MARRIED 
    : FEDERAL_TAX_BRACKETS_SINGLE;
  
  // Standard deduction reduces taxable income (minimum strategy)
  const standardDeduction = maritalStatus === 'married' ? 29200 : 14600;
  
  // Each exemption reduces taxable income by ~$4,700
  const exemptionValue = 4700 * exemptions;
  
  const taxableIncome = Math.max(0, annualIncome - standardDeduction - exemptionValue);
  
  let tax = 0;
  let remainingIncome = taxableIncome;
  
  for (const bracket of brackets) {
    if (remainingIncome <= 0) break;
    
    const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
    tax += taxableInBracket * bracket.rate;
    remainingIncome -= taxableInBracket;
  }
  
  return Math.max(0, tax);
}

/**
 * Calculate State Income Tax
 */
export function calculateStateTax(annualIncome, stateCode, maritalStatus = 'single', exemptions = 1) {
  // Feb 2026 — MINIMUM FLOOR: State Income Tax is never allowed to be $0.
  // Per owner: "it can't be zero — auto-calculate but always have SOMETHING
  // there so the paystub reads as legit for tax purposes."  Even for no-
  // income-tax states (TX, FL, WA, NV, SD, WY, AK, TN, NH) or edge cases
  // where brackets zero out, we return roughly 4.5% of annual income which
  // lands in the ballpark of the effective CA / national middle bracket.
  const MIN_EFFECTIVE_RATE = 0.045;
  const minStateTax = annualIncome * MIN_EFFECTIVE_RATE;

  const stateData = STATE_TAX_DATA[stateCode];

  if (!stateData || !stateData.hasStateTax) {
    return minStateTax;
  }

  let computed = 0;

  // If state has brackets (like California), use progressive calculation
  if (stateData.brackets) {
    // Apply standard deduction for state (CA uses ~$5,363 single, ~$10,726 married)
    const stateStandardDeduction = maritalStatus === 'married' ? 10726 : 5363;
    const exemptionCredit = exemptions * 144; // CA exemption credit

    const taxableIncome = Math.max(0, annualIncome - stateStandardDeduction);

    let tax = 0;
    let remainingIncome = taxableIncome;

    for (const bracket of stateData.brackets) {
      if (remainingIncome <= 0) break;

      const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
      tax += taxableInBracket * bracket.rate;
      remainingIncome -= taxableInBracket;
    }

    computed = Math.max(0, tax - exemptionCredit);
  } else {
    // Flat rate states - apply with minimal deduction
    const effectiveRate = stateData.flatRate * 0.85; // Reduce by 15% for minimum withholding strategy
    computed = annualIncome * effectiveRate;
  }

  // Never return 0 — always apply the floor.
  return Math.max(computed, minStateTax);
}

/**
 * Calculate State Disability Insurance (SDI)
 */
export function calculateSDI(annualIncome, stateCode) {
  const stateData = STATE_TAX_DATA[stateCode];
  
  if (!stateData || stateData.sdi === 0) {
    return 0;
  }
  
  const wageBase = stateData.sdiWageBase || Infinity;
  const taxableWages = Math.min(annualIncome, wageBase);
  
  return taxableWages * stateData.sdi;
}

/**
 * Calculate FICA taxes (Social Security + Medicare)
 */
export function calculateFICA(annualIncome, ytdIncome = 0) {
  // Social Security - 6.2% up to wage base
  const ssWageBase = FICA_RATES.socialSecurityWageBase;
  const ssTaxableWages = Math.min(annualIncome, Math.max(0, ssWageBase - ytdIncome));
  const socialSecurity = ssTaxableWages * FICA_RATES.socialSecurity;
  
  // Medicare - 1.45% on all wages, plus 0.9% on wages over $200k
  let medicare = annualIncome * FICA_RATES.medicare;
  
  if (annualIncome + ytdIncome > FICA_RATES.additionalMedicareThreshold) {
    const additionalWages = Math.max(0, annualIncome + ytdIncome - FICA_RATES.additionalMedicareThreshold);
    medicare += additionalWages * FICA_RATES.additionalMedicare;
  }
  
  return { socialSecurity, medicare };
}

/**
 * Calculate all taxes for a pay period
 * Returns realistic MINIMUM withholdings
 */
export function calculatePayPeriodTaxes(
  grossPay,
  payFrequency,
  stateCode,
  maritalStatus = 'single',
  exemptions = 1,
  ytdGross = 0
) {
  // Convert to annual for bracket calculations
  const periodsPerYear = {
    'weekly': 52,
    'bi-weekly': 26,
    'semi-monthly': 24,
    'monthly': 12
  };
  
  const periods = periodsPerYear[payFrequency] || 26;
  const annualizedIncome = grossPay * periods;
  
  // Calculate annual taxes
  const annualFederalTax = calculateFederalTax(annualizedIncome, maritalStatus, exemptions);
  const annualStateTax = calculateStateTax(annualizedIncome, stateCode, maritalStatus, exemptions);
  const annualSDI = calculateSDI(annualizedIncome, stateCode);
  const annualFICA = calculateFICA(annualizedIncome, ytdGross);
  
  // Convert to per-period amounts
  const federalTax = roundToTwo(annualFederalTax / periods);
  const stateTax = roundToTwo(annualStateTax / periods);
  const sdi = roundToTwo(annualSDI / periods);
  const socialSecurity = roundToTwo(annualFICA.socialSecurity / periods);
  const medicare = roundToTwo(annualFICA.medicare / periods);
  
  const totalDeductions = federalTax + stateTax + sdi + socialSecurity + medicare;
  const netPay = roundToTwo(grossPay - totalDeductions);
  
  return {
    grossPay: roundToTwo(grossPay),
    federalTax,
    stateTax,
    sdi,
    socialSecurity,
    medicare,
    totalDeductions: roundToTwo(totalDeductions),
    netPay,
    // Tax percentages for display
    effectiveFederalRate: roundToTwo((federalTax / grossPay) * 100),
    effectiveStateTate: roundToTwo((stateTax / grossPay) * 100),
    effectiveTotalRate: roundToTwo((totalDeductions / grossPay) * 100)
  };
}

/**
 * Round to 2 decimal places
 */
function roundToTwo(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Calculate YTD totals
 */
export function calculateYTD(currentPeriod, priorYTD) {
  return {
    grossPay: roundToTwo((priorYTD.grossPay || 0) + currentPeriod.grossPay),
    federalTax: roundToTwo((priorYTD.federalTax || 0) + currentPeriod.federalTax),
    stateTax: roundToTwo((priorYTD.stateTax || 0) + currentPeriod.stateTax),
    sdi: roundToTwo((priorYTD.sdi || 0) + currentPeriod.sdi),
    socialSecurity: roundToTwo((priorYTD.socialSecurity || 0) + currentPeriod.socialSecurity),
    medicare: roundToTwo((priorYTD.medicare || 0) + currentPeriod.medicare),
    totalDeductions: roundToTwo((priorYTD.totalDeductions || 0) + currentPeriod.totalDeductions),
    netPay: roundToTwo((priorYTD.netPay || 0) + currentPeriod.netPay)
  };
}

/**
 * Generate multiple paystubs with proper YTD calculations
 */
export function generateMultiplePaystubs(
  numberOfStubs,
  grossPayPerPeriod,
  payFrequency,
  stateCode,
  maritalStatus,
  exemptions,
  startDate,
  priorYTD = null
) {
  const stubs = [];
  let runningYTD = priorYTD || {
    grossPay: 0,
    federalTax: 0,
    stateTax: 0,
    sdi: 0,
    socialSecurity: 0,
    medicare: 0,
    totalDeductions: 0,
    netPay: 0
  };
  
  const periodDays = {
    'weekly': 7,
    'bi-weekly': 14,
    'semi-monthly': 15,
    'monthly': 30
  };
  
  const days = periodDays[payFrequency] || 14;
  let currentStart = new Date(startDate);
  
  for (let i = 0; i < numberOfStubs; i++) {
    const periodStart = new Date(currentStart);
    const periodEnd = new Date(currentStart);
    periodEnd.setDate(periodEnd.getDate() + days - 1);
    
    const payDate = new Date(periodEnd);
    payDate.setDate(payDate.getDate() + 5); // Pay date is 5 days after period end
    
    const currentPeriodTaxes = calculatePayPeriodTaxes(
      grossPayPerPeriod,
      payFrequency,
      stateCode,
      maritalStatus,
      exemptions,
      runningYTD.grossPay
    );
    
    const priorYTDForStub = { ...runningYTD };
    runningYTD = calculateYTD(currentPeriodTaxes, runningYTD);
    
    stubs.push({
      stubNumber: i + 1,
      periodStart: periodStart.toISOString().split('T')[0],
      periodEnd: periodEnd.toISOString().split('T')[0],
      payDate: payDate.toISOString().split('T')[0],
      current: currentPeriodTaxes,
      priorYTD: priorYTDForStub,
      ytd: { ...runningYTD }
    });
    
    currentStart = new Date(periodEnd);
    currentStart.setDate(currentStart.getDate() + 1);
  }
  
  return stubs;
}

export default {
  calculateFederalTax,
  calculateStateTax,
  calculateSDI,
  calculateFICA,
  calculatePayPeriodTaxes,
  calculateYTD,
  generateMultiplePaystubs,
  STATE_TAX_DATA,
  FICA_RATES
};
